import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useOrganization } from '../../context/OrgContext';
import {
  LuFolderPlus,
  LuGitBranch,
  LuPlus,
  LuSettings,
  LuUserPlus,
  LuChevronDown,
  LuFolder,
} from "react-icons/lu";
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import InviteModal from '../../components/modals/admin/InviteModal';
import NewModal from '../../components/modals/admin/NewModuleModal';
import { SectionItem, WorkflowGrid } from '../../components/dashboard';

const AdminDashboard = () => {
  const { institution } = useOrganization();
  const navigate = useNavigate();

  // Modal state management
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showNewModal, setShowNewModal] = useState(false);

  // Sections and workflows data
  const [sections, setSections] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [sectionsLoading, setSectionsLoading] = useState(true);
  const [workflowsLoading, setWorkflowsLoading] = useState(true);

  // Fetch sections from institution document
  const fetchSections = async () => {
    if (!institution?.id) return;

    try {
      setSectionsLoading(true);
      const institutionRef = doc(db, `colleges/${institution.id}`);
      const institutionDoc = await getDoc(institutionRef);
      const institutionData = institutionDoc.data() || {};
      const sectionsData = institutionData.sections || [];

      setSections(sectionsData);
    } catch (error) {
      console.error('Error fetching sections:', error);
      setSections([]);
    } finally {
      setSectionsLoading(false);
    }
  };

  // Fetch workflows from workflows collection
  const fetchWorkflows = async () => {
    if (!institution?.id) return;

    try {
      setWorkflowsLoading(true);
      const workflowsRef = collection(db, `colleges/${institution.id}/workflows`);
      const workflowsSnap = await getDocs(workflowsRef);

      const workflowsData = workflowsSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Sort workflows by creation date
      workflowsData.sort((a, b) => {
        const aDate = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
        const bDate = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
        return bDate - aDate; // Most recent first
      });

      setWorkflows(workflowsData);
    } catch (error) {
      console.error('Error fetching workflows:', error);
      setWorkflows([]);
    } finally {
      setWorkflowsLoading(false);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchSections();
    fetchWorkflows();
  }, [institution?.id]);

  // Handle settings navigation
  const handleSettingsClick = () => {
    navigate('/settings');
  };

  // Handle general invite modal
  const handleInviteClick = () => {
    setShowInviteModal(true);
  };

  const handleCloseInviteModal = () => {
    setShowInviteModal(false);
  };

  // Handle new modal
  const handleNewClick = () => {
    setShowNewModal(true);
  };

  const handleCloseNewModal = () => {
    setShowNewModal(false);
  };

  // Handle successful item addition
  const handleItemAdded = () => {
    // Refresh both sections and workflows
    fetchSections();
    fetchWorkflows();
    setShowNewModal(false);
  };

  // Handle section click
  const handleSectionClick = (section) => {
    navigate(`/${section.slug}`);
  };

  // Handle workflow click
  const handleWorkflowClick = (workflow) => {
    navigate(`/workflows/${workflow.slug || workflow.id}`);
  };

  // Format date helper
  const formatDate = (date) => {
    if (!date) return 'Unknown';

    const dateObj = date.toDate ? date.toDate() : new Date(date);
    return dateObj.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <div className="h-full w-full text-white flex flex-col overflow-hidden">
      {/* Main Header */}
      <div className="w-full border-b border-[#ffffff10] px-8 pt-8 pb-4 flex justify-between">
        <div className="text-3xl font-medium">{institution?.fullName}</div>
        <div className="flex space-x-3">
          <div
            className="py-1.5 px-2.5 h-fit aspect-square flex items-center justify-center border border-[#ffffff10] rounded-lg hover:bg-[#ffffff10] transition hover:border-[#ffffff15] cursor-pointer active:scale-95 transform duration-150 ease-out"
            onClick={handleSettingsClick}
          >
            <LuSettings/>
          </div>
          <div
            className="px-4 py-1.5 h-fit border border-[#ffffff10] space-x-2 rounded-lg flex items-center justify-center hover:bg-[#ffffff10] transition hover:border-[#ffffff15] cursor-pointer active:scale-95 transform duration-150 ease-out"
            onClick={handleInviteClick}
          >
            <LuUserPlus/>
            <div>Invite</div>
          </div>
          <div
            className="px-4 py-1.5 h-fit border border-[#ffffff10] space-x-2 rounded-lg flex items-center justify-center hover:bg-[#ffffff10] transition hover:border-[#ffffff15] cursor-pointer active:scale-95 transform duration-150 ease-out"
            onClick={handleNewClick}
          >
            <LuPlus/>
            <div>New</div>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Navigation - Sections */}
        <div className="w-80 border-r border-[#ffffff10] flex flex-col">

        {/* Sections List */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="flex items-center space-x-2 mb-4">
            <LuFolderPlus className="w-4 h-4 text-blue-400" />
            <span className="text-sm text-[#ffffff70]">
              {sectionsLoading ? (
                <div className="h-4 w-16 bg-[#ffffff20] rounded animate-pulse"></div>
              ) : (
                `${sections.length} ${sections.length === 1 ? 'Section' : 'Sections'}`
              )}
            </span>
          </div>

          {sectionsLoading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4].map(placeholder => (
                <div key={placeholder} className="p-3 bg-[#ffffff10] rounded-lg animate-pulse">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-[#ffffff20] rounded-lg"></div>
                    <div className="flex-1">
                      <div className="h-4 bg-[#ffffff20] rounded mb-2"></div>
                      <div className="h-3 bg-[#ffffff10] rounded w-2/3"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : sections.length === 0 ? (
            <div className="text-center py-8">
              <LuFolder className="w-12 h-12 text-[#ffffff30] mx-auto mb-3" />
              <div className="text-[#ffffff50] text-sm">No sections yet</div>
              <div
                onClick={handleNewClick}
                className="mt-3 px-3 py-1.5 text-sm border border-[#ffffff15] rounded-lg hover:bg-[#ffffff10] transition cursor-pointer inline-flex items-center space-x-2"
              >
                <LuPlus className="w-4 h-4" />
                <span>Create Section</span>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {sections.map(section => (
                <SectionItem
                  key={section.slug || section.id}
                  name={section.name}
                  onClick={() => handleSectionClick(section)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Main Content - Workflows */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Workflows Header */}
        <div className="px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <LuGitBranch className="w-4 h-4 text-green-400"/>
              <span className="text-sm text-[#ffffff70]">
              {workflowsLoading ? (
                  <div className="h-4 w-16 bg-[#ffffff20] rounded animate-pulse"></div>
              ) : (
                  `${workflows.length} ${workflows.length === 1 ? 'Workflow' : 'Worflows'}`
              )}
            </span>
            </div>
            <div className="flex items-center space-x-2 text-sm group cursor-pointer">
              <span className={"text-[#ffffff50] group-hover:text-white transition"}>Recent Activity</span>
              <LuChevronDown className="w-4 h-4 text-[#ffffff50] group-hover:text-white transition"/>
            </div>
          </div>
        </div>

        {/* Workflows Grid */}
        <div className="flex-1 overflow-y-auto px-8">
          {workflowsLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[1, 2, 3, 4, 5, 6].map(placeholder => (
                    <div className="w-full h-48 relative" key={placeholder}>
                      <div
                          className="p-4 w-full h-full bg-[#1e1e1e] border border-[#ffffff10] rounded-xl flex flex-col justify-between animate-pulse">
                    <div className="flex space-x-2 items-center">
                      <div className="p-3 bg-[#ffffff20] rounded-lg">
                        <div className="w-6 h-6 bg-[#ffffff30] rounded"></div>
                      </div>
                      <div className="flex-1">
                        <div className="h-5 bg-[#ffffff20] rounded mb-2"></div>
                        <div className="h-4 bg-[#ffffff10] rounded w-3/4"></div>
                      </div>
                    </div>
                    <div className="flex space-x-2 items-center">
                      <div className="p-3">
                        <div className="w-5 h-5 bg-[#ffffff20] rounded-full"></div>
                      </div>
                      <div className="flex-1">
                        <div className="h-4 bg-[#ffffff10] rounded w-1/2"></div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : workflows.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full bg-[#ffffff10] border border-[#ffffff15] rounded-xl">
              <LuGitBranch className="w-16 h-16 text-[#ffffff30] mb-4" />
              <div className="text-2xl mb-2">No Workflows</div>
              <div className="text-[#ffffff50] mb-6">Create your first workflow to get started</div>
              <div
                onClick={handleNewClick}
                className="px-4 py-2 font-medium border border-[#ffffff15] space-x-2 rounded-lg flex items-center justify-center hover:bg-[#ffffff20] transition cursor-pointer active:scale-95 transform duration-150 ease-out"
              >
                <LuPlus />
                <div>Create Workflow</div>
              </div>
            </div>
          ) : (
            <WorkflowGrid
              workflows={workflows}
              isLoading={workflowsLoading}
              onWorkflowClick={handleWorkflowClick}
              onNewWorkflowClick={handleNewClick}
            />
          )}
        </div>
      </div>

        {/* Modals */}
        {showInviteModal && (
            <InviteModal
                isOpen={showInviteModal}
                onClose={handleCloseInviteModal}
            />
        )}

        {showNewModal && (
            <NewModal
                isOpen={showNewModal}
                onClose={handleCloseNewModal}
                onItemAdded={handleItemAdded}
            />
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;